import { useReveal } from '../hooks/useReveal';
import { Lightbox } from './shared/Lightbox';
import './AgentLoop.scss';

const AGENT_SHOT = '/images/screenshots/agent_task_prompt.png';

/**
 * "Close the loop" section — the agent hand-off story. Turns the review
 * feedback loop into an actionable one: any comment can become a task,
 * and the widget sidebar exports every unresolved task as a ready-to-run
 * agent prompt (or a scoped JSON link) that an AI agent resolves in
 * place.
 *
 * Right side wraps the sample "agent prompt output" screenshot in a
 * terminal-style frame, clickable into the shared <Lightbox>.
 */
export function AgentLoop() {
	const ref = useReveal<HTMLDivElement>();
	return (
		<section class="m-loop" id="agents">
			<div class="m-loop__inner" ref={ref}>
				<div class="m-loop__copy">
					<span class="m-loop__eyebrow">close the loop</span>
					<h2 class="m-loop__title">Hand your review tasks straight to an AI agent.</h2>
					<p class="m-loop__body">
						Flip any comment into a task. When it's time to act, the widget
						sidebar exports every unresolved task for you — for a single page, or
						the entire project (every page at once). One click copies a
						ready-to-run <strong>agent prompt</strong> or a scoped link. Paste it
						into Claude, Cursor, or your agent of choice and it fixes each item in
						place, then marks the task resolved. The loop closes itself.
					</p>

					<ul class="m-loop__list">
						<li class="m-loop__list-item">
							<span class="m-loop__check" aria-hidden="true" />
							<span>
								<strong>Copy tasks as prompt</strong> — an instruction block
								listing every open task, its exact anchor, and how to resolve it.
								Grab it for just the current page, or for every page in the
								project in one click.
							</span>
						</li>
						<li class="m-loop__list-item">
							<span class="m-loop__check" aria-hidden="true" />
							<span>
								<strong>Copy agent link</strong> — a scoped, revocable URL an
								agent can poll for the same task list as JSON.
							</span>
						</li>
						<li class="m-loop__list-item">
							<span class="m-loop__check" aria-hidden="true" />
							<span>
								Prefer the terminal? The same task prompt + JSON are available
								straight from the <strong>PageLoop CLI</strong> and REST API —
								drop it into CI or a script.
							</span>
						</li>
					</ul>

					<div class="m-loop__chips">
						<code class="m-loop__chip">GET /api/v1/comments/prompt</code>
						<span class="m-loop__chip m-loop__chip--soon">
							<span class="m-loop__chip-dot" />
							PageLoop MCP — coming soon
						</span>
					</div>
				</div>

				<div class="m-loop__visual">
					<Lightbox
						src={AGENT_SHOT}
						alt="Sample PageLoop agent prompt output listing two unresolved review tasks with their anchors and resolve instructions"
						caption="The agent prompt PageLoop generates for a page's unresolved tasks."
						triggerClass="m-loop__shot-trigger"
					>
						<figure class="m-loop__shot">
							<figcaption class="m-loop__shot-bar">
								<span class="m-loop__shot-dot m-loop__shot-dot--r" />
								<span class="m-loop__shot-dot m-loop__shot-dot--y" />
								<span class="m-loop__shot-dot m-loop__shot-dot--g" />
								<span class="m-loop__shot-title">agent prompt output</span>
							</figcaption>
							<div class="m-loop__shot-body">
								<img
									class="m-loop__shot-img"
									src={AGENT_SHOT}
									alt=""
									loading="lazy"
								/>
							</div>
						</figure>
					</Lightbox>
				</div>
			</div>
		</section>
	);
}
